import React from 'react';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import VoucherPublicPreviewCard from '~/components/Vouchers/VoucherPublicPreviewCard';

import type { PreviewPublicVoucher } from '~/services/PromoService';

type Props = {
  localError: string;
  isError: boolean;
  errorHeadline: string;
  errorDetailBlock: string;
  publicVoucher?: PreviewPublicVoucher;
  isPending?: boolean;
  emptyMessage: string;
  loadingMessage?: string;
};

export default function VoucherPreviewContent({
  localError,
  isError,
  errorHeadline,
  errorDetailBlock,
  publicVoucher,
  isPending = false,
  emptyMessage,
  loadingMessage,
}: Props) {
  return (
    <Stack spacing={2}>
      {localError ? <Alert severity="warning">{localError}</Alert> : null}
      {isError ? (
        <Alert severity="error">
          <Typography variant="body2">{errorHeadline}</Typography>
          {errorDetailBlock ? (
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, mt: 1 }}>
              {errorDetailBlock}
            </Box>
          ) : null}
        </Alert>
      ) : null}
      <Box sx={{ position: 'relative', minHeight: publicVoucher ? undefined : 72 }}>
        {publicVoucher ? <VoucherPublicPreviewCard voucher={publicVoucher} /> : null}
        {!publicVoucher && !localError && !isError && !isPending ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : null}
        {isPending && loadingMessage ? (
          <Box
            sx={(theme) => ({
              position: publicVoucher ? 'absolute' : 'relative',
              inset: publicVoucher ? 0 : undefined,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: publicVoucher ? undefined : 72,
              borderRadius: publicVoucher ? 7 : 1,
              bgcolor: alpha(theme.palette.background.paper, 0.72),
              backdropFilter: publicVoucher ? 'blur(1px)' : undefined,
            })}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage}
              </Typography>
            </Stack>
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
}
